import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Image as ImageIcon, Video, Send, Star, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DAILY, formatTRDate, formatDMY, todayISO, mediaSrc, isVideo, TaskCategoryLabel } from "./shared";

function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

export default function TaskDetailView({ task, onReload }) {
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [savingDaily, setSavingDaily] = useState(false);
  const inputRef = useRef();

  const daily = task.daily_status || [];
  const review = (task.feedback || []).find((f) => f.status === "approved" || f.status === "rejected" || f.rating || f.review_comment);

  const setDaily = async (status) => {
    setSavingDaily(true);
    try {
      await api.post(`/parent/assignments/${task.id}/daily`, { date: todayISO(), status });
      toast.success("Durum kaydedildi");
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingDaily(false); }
  };

  const pick = (accept) => { if (inputRef.current) { inputRef.current.accept = accept; inputRef.current.click(); } };
  const addFiles = (e) => setFiles((p) => [...p, ...Array.from(e.target.files || [])]);
  const removeFile = (i) => setFiles((p) => p.filter((_, idx) => idx !== i));

  const send = async () => {
    if (!comment.trim() && files.length === 0) { toast.error("Yorum veya medya ekleyin"); return; }
    setSending(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      await api.post(`/parent/assignments/${task.id}/feedback`, { comment, media_urls: urls });
      toast.success("Öğretmene gönderildi ✓");
      setComment(""); setFiles([]); onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSending(false); }
  };

  const curDaily = daily.length ? daily[daily.length - 1].status : null;

  return (
    <div data-testid="parent-task-detail">
      <Card className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
        <TaskCategoryLabel name={task.task?.area} className="mb-2" />
        <h1 className="font-heading text-xl font-bold text-slate-900">{task.task?.title}</h1>
        {task.task?.description && <p className="mt-1.5 text-sm text-slate-500">{task.task.description}</p>}
        {task.note && <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">Not: {task.note}</p>}
      </Card>

      {/* Durum selector */}
      <h2 className="mb-2 mt-6 font-heading font-bold text-slate-900">Durum</h2>
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(DAILY).map(([k, v]) => {
          const active = curDaily === k;
          return (
            <button key={k} disabled={savingDaily} data-testid={`daily-${k}`} onClick={() => setDaily(k)}
              className={`flex items-center justify-center gap-1.5 rounded-xl border py-2.5 text-sm font-semibold transition-colors ${active ? `${v.solid} border-transparent` : `bg-white text-slate-500 ${v.ring}`}`}>
              <span className={`flex h-4 w-4 items-center justify-center rounded-full ${active ? "bg-white" : v.dot}`}><v.Icon className={`h-2.5 w-2.5 ${active ? v.iconText : "text-white"}`} strokeWidth={3} /></span>
              {v.label}
            </button>
          );
        })}
      </div>

      {/* Günlük Takip */}
      <h2 className="mb-2 mt-6 font-heading font-bold text-slate-900">Günlük Takip</h2>
      <Card className="rounded-2xl border-slate-200/60 px-4 py-1 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
        {daily.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">Henüz kayıt yok.</p> :
          daily.map((d) => (
            <div key={d.id} className="flex items-center justify-between border-b border-slate-100 py-3 last:border-0">
              <span className="text-sm text-slate-700">{formatTRDate(d.date)}</span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${DAILY[d.status]?.chip}`}>{DAILY[d.status]?.label}</span>
            </div>
          ))}
      </Card>

      {/* Öğretmene Bildirim */}
      <h2 className="mb-2 mt-6 font-heading font-bold text-slate-900">Öğretmene Bildirim (Kanıt Ekle)</h2>
      <Card className="rounded-2xl border-slate-200/60 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
        <input ref={inputRef} type="file" multiple onChange={addFiles} className="hidden" data-testid="respond-file-input" />
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => pick("image/*")} data-testid="add-photo" className="flex-1 rounded-xl border-slate-200"><ImageIcon className="mr-1.5 h-4 w-4 text-violet-600" /> Fotoğraf</Button>
          <Button type="button" variant="outline" onClick={() => pick("video/*")} data-testid="add-video" className="flex-1 rounded-xl border-slate-200"><Video className="mr-1.5 h-4 w-4 text-blue-600" /> Video</Button>
        </div>
        {files.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {files.map((f, i) => (
              <div key={i} className="relative flex items-center gap-1 rounded-lg bg-slate-100 py-1 pl-2 pr-1 text-xs">
                <Paperclip className="h-3 w-3 text-slate-500" /><span className="max-w-[120px] truncate">{f.name}</span>
                <button type="button" onClick={() => removeFile(i)} className="rounded p-0.5 hover:bg-slate-200"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="respond-comment"
          placeholder="Açıklama yazın (örn. bugün çok istekliydi)..." className="mt-3 min-h-[90px] rounded-xl bg-slate-50" />
        <Button onClick={send} disabled={sending} data-testid="respond-submit" className="mt-3 h-12 w-full rounded-xl bg-violet-600 text-base font-semibold text-white hover:bg-violet-700">
          {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Öğretmene Gönder</>}
        </Button>
      </Card>

      {/* Öğretmen Geri Bildirimi */}
      <h2 className="mb-2 mt-6 font-heading font-bold text-slate-900">Öğretmen Geri Bildirimi</h2>
      {review && (review.rating || review.review_comment || review.review_result) ? (
        <Card className="rounded-2xl border-slate-200/60 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="teacher-review">
          {review.review_result && DAILY[review.review_result] && (
            <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${DAILY[review.review_result].chip}`}>{DAILY[review.review_result].label}</span>
          )}
          {review.rating ? <div className="mt-2"><Stars n={review.rating} /></div> : null}
          {review.review_comment && <p className="mt-2 font-heading font-semibold text-slate-900">{review.review_comment}</p>}
        </Card>
      ) : (
        <Card className="rounded-2xl border-dashed p-6 text-center text-sm text-slate-400">Öğretmen henüz geri bildirim vermedi.</Card>
      )}

      {/* Gönderilen Notlar */}
      {(() => {
        const sent = (task.feedback || []).filter((f) => f.comment || f.media_urls?.length)
          .slice().sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
        if (sent.length === 0) return null;
        return (
          <>
            <h2 className="mb-2 mt-6 font-heading font-bold text-slate-900">Gönderilen Notlar</h2>
            <div className="space-y-3">
              {sent.map((f) => (
                <Card key={f.id} data-testid={`sent-note-${f.id}`} className="rounded-2xl border-slate-200/60 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                  {f.comment && <p className="text-[15px] text-slate-800">{f.comment}</p>}
                  {f.media_urls?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {f.media_urls.map((u, i) => isVideo(u)
                        ? <video key={i} src={mediaSrc(u)} controls className="h-24 w-24 rounded-xl bg-black object-cover" />
                        : <img key={i} src={mediaSrc(u)} alt="" className="h-24 w-24 rounded-xl object-cover" />)}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-slate-400">{formatDMY(f.created_at)}</p>
                </Card>
              ))}
            </div>
          </>
        );
      })()}
    </div>
  );
}
